import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classes from "./StudentSelection.scss";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import ProfileCascade from "UIComponents/ProfileCascade";
import GroupedList from "UIComponents/GroupedList";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import update from "immutability-helper";
import {
  getCourseUserMapFromCache,
  getAssignedStudentsFromCache,
} from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import {
  getCourseUserMapQuery,
  getAssignedStudentsQuery,
} from "ClassRoom/modules/ClassRoomQuery";
import {
  generateRandomId,
  getUpdatedArchivedFilters,
  localSearch,
} from "Utils";
import {
  getDefaultOrderBy,
  getFormattedUserName,
} from "Utils/GetFormattedUserName";
import { Avatar, Checkbox as DSCheckbox, Tag } from "@toddle-edu/ds-web";
import { GENERIC_TAGS_INFO } from "modules/Services";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserArchivalStatus,
} from "modules/CommonSelectors";
import { ASC, CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { colors } from "Constants";

const styles = {
  avatarStyles: {
    size: "small",
    shape: "circle",
    iconType: "individual",
    shade: "light",
    randomizeColor: true,
  },
  groupedListStyles: {
    containerStyle: { padding: "0px" },
    leftContainerStyle: { alignItems: "center" },
    optionContainerStyle: { padding: "8px 0", alignItems: "center" },
    groupOptionContainerStyle: {
      padding: "8px 0",
      borderBottom: `1px solid ${colors.borderSubtle}`,
      alignItems: "center",
    },
  },
};

const StudentSelectionItem = React.memo(
  ({ student, toggleCheckbox, isSelected, t, testId }) => {
    const { profileImage, id, isArchived, alertTypes = [] } = student;
    const studentName = getFormattedUserName({
      user: student,
    });

    const isStudentArchived = _.has(student, "isArchivedV2")
      ? getUserArchivalStatus(student)
      : isArchived;

    const tags = _.get(student, "tags", []);
    const studentAlertsConfig = {
      alertTypes,
      studentId: id,
    };

    return (
      <div
        className={classes.studentItemRow}
        data-test-id={`${testId}-student-${id}-container`}
        onClick={toggleCheckbox}
      >
        <div className={classes.avatarContainer}>
          <div className={classes.avatarInnerContainer}>
            <UserNameAvatar
              id={id}
              profileImage={profileImage}
              name={studentName}
              shouldDynamicColor={true}
              isArchived={isStudentArchived}
              showAvatar={true}
              showArchivedText={true}
              avatarStyles={styles.avatarStyles}
              customNameClass={classes.labelContainer}
              studentAlertsConfig={studentAlertsConfig}
            />
            {!_.isEmpty(tags) && (
              <div className={classes.tagsContainer}>
                {_.map(tags, item => {
                  return (
                    <Tag
                      key={item.label}
                      size={"small"}
                      _legacy_size={"xx-small"}
                      color={_.get(
                        GENERIC_TAGS_INFO,
                        `${item.label}.color`,
                        "teal"
                      )}
                    >
                      {item.label}
                    </Tag>
                  );
                })}
              </div>
            )}
          </div>
          <DSCheckbox
            testId={`${testId}-student-${id}`}
            isChecked={isSelected}
          />
        </div>
      </div>
    );
  }
);

StudentSelectionItem.displayName = "StudentSelectionItem";

const StudentSelection = React.memo(props => {
  const {
    mode,
    includedStudents,
    students: _students,
    t,
    updateStudentArray,
    selectedItemType,
    searchTerm,
    defaultStudentNameOrderPreference,
    isData,
    isLoading,
    groupedStudents = [],
    testId,
  } = props;

  const students = useMemo(() => {
    if (!mode || mode === "create") return _students;

    return getSearchedStudents({
      students: _students,
      defaultStudentNameOrderPreference,
      searchText: searchTerm,
    });
  }, [mode, _students, defaultStudentNameOrderPreference, searchTerm]);

  const toggleCheckbox = item => {
    let newIncludedStudents = [...includedStudents];
    const index = _.findIndex(newIncludedStudents, includedStudent => {
      return selectedItemType == "OBJECT"
        ? includedStudent.id == item.id
        : includedStudent == item.id;
    });
    if (index != -1) {
      newIncludedStudents = update(newIncludedStudents, {
        $splice: [[index, 1]],
      });
    } else {
      newIncludedStudents = update(newIncludedStudents, {
        $splice: [[index, 0, item]],
      });
    }
    updateStudentArray(newIncludedStudents);
  };

  const toggleAllCheckbox = () => {
    if (_.get(includedStudents, "length", 0) == _.get(students, "length", 0)) {
      updateStudentArray([]);
    } else {
      updateStudentArray(
        selectedItemType == "OBJECT"
          ? students
          : _.map(students, student => student.id)
      );
    }
  };

  const isAllSelected =
    _.get(includedStudents, "length", 0) == _.get(students, "length", 0);

  const renderStudents = () => {
    if (!_.isEmpty(groupedStudents)) {
      return (
        <GroupedList
          testId={testId}
          items={groupedStudents}
          selectedItems={includedStudents}
          setSelectedItems={updateStudentArray}
          isData={isData}
          isLoading={isLoading}
          itemContainerClass={classes.itemContainerClass}
          {...styles.groupedListStyles}
        />
      );
    }

    return _.map(students, student => {
      const isSelected =
        _.findIndex(includedStudents, includedStudent =>
          selectedItemType == "OBJECT"
            ? includedStudent.id == student.id
            : includedStudent == student.id
        ) >= 0;

      return (
        <StudentSelectionItem
          testId={testId}
          student={student}
          toggleCheckbox={() => toggleCheckbox(student)}
          key={student.id}
          isSelected={isSelected}
          t={t}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
        />
      );
    });
  };

  return (
    <div className={classes.studentFeedContainer}>
      {_.isEmpty(searchTerm) && (
        <div
          className={classes.studentItemRow}
          data-test-id={`${testId}-selectAllStudents-container`}
          onClick={toggleAllCheckbox}
        >
          <div className={classes.avatarContainer}>
            <div className={classes.avatarInnerContainer}>
              <Avatar
                size={"small"}
                theme={"light"}
                shade={"light"}
                shape={"circle"}
                color={"neutral"}
                iconType={"group"}
              />
              <div className={classes.demiBoldLabelContainer}>
                {t("common:all_label", {
                  label: t("common:student_plural"),
                })}
              </div>
            </div>
            <DSCheckbox
              testId={`${testId}-selectAllStudents`}
              isChecked={isAllSelected}
            />
          </div>
        </div>
      )}
      {renderStudents()}
    </div>
  );
});

StudentSelection.displayName = "StudentSelection";

StudentSelection.propTypes = {
  includedStudents: PropTypes.array,
  isMultiSelect: PropTypes.bool,
  selectedItemType: PropTypes.string,
  testId: PropTypes.string,
};

StudentSelection.defaultProps = {
  includedStudents: [],
  isMultiSelect: true,
  selectedItemType: "ID",
  testId: "classRoom-addAssignment-studentSelection",
};

const mapActionCreators = {};

const mapStateToProps = (state, ownProps) => {
  const { mode } = ownProps;
  const searchText = _.get(ownProps, "searchTerm");
  const assignmentId = _.get(ownProps, "assignmentId");
  const assignmentTags = _.get(ownProps, "assignmentTags");

  // in case of mode === "create" we get students from getCourseUserMapQuery and we can pass searchText to query
  // in case of mode === "edit" we get students from getCourseUserMapQuery (course students) and getAssignedStudentsQuery (students assigned in the assignment)
  // so it is not possible use searchText filter because we have two queries hence we do local search in edit case
  const archivedFilters =
    getUpdatedArchivedFilters(
      {
        ...(mode === "create" || !mode ? { searchText } : {}),
      },
      { entityType: "ASSIGNMENT", entityId: assignmentId }
    ) || {};

  const academicYearId = getSelectedAcademicYearId(state);

  const userArchivedFilters = {
    academicYearId,
  };

  const courseUserMapFilters = {
    ...archivedFilters,
    tags: assignmentTags,
    userTypes: ["student"],
    orderBy: getDefaultOrderBy({ type: "student" }),
    orderByDirection: ASC,
    academicYearIds: academicYearId ? [academicYearId] : undefined,
  };

  return {
    parentId: ownProps.parentId,
    isData: true,
    isLoading: false,
    courseUserMapFilters,
    searchText,
    curriculumProgramType: getCurrentCurriculumProgramType(state),
    userArchivedFilters,
  };
};

const getStudents = ({ t, data, curriculumProgramType }) => {
  if (!data) {
    return [];
  }

  const studentsList = _.map(data, item => {
    return {
      ..._.get(item, "node.user", {}),
      tags: _.get(item, "node.tags", []),
    };
  });

  const activeStudents = [];
  const archivedStudents = [];

  _.forEach(studentsList, student => {
    const { isArchived = false } = student;

    const isStudentArchived = _.has(student, "isArchivedV2")
      ? getUserArchivalStatus(student)
      : isArchived;

    if (isStudentArchived) {
      archivedStudents.push(student);
    } else {
      activeStudents.push(student);
    }
  });

  const allStudents = [...activeStudents, ...archivedStudents];

  let groupedStudents = _.groupBy(allStudents, student =>
    _.get(student, "tags.0.label")
  );

  const shouldGroupStudents =
    curriculumProgramType === CURRICULUM_TYPE_DP &&
    _.every(allStudents, student => !_.isEmpty(student?.tags)) &&
    _.size(groupedStudents) > 1;

  if (shouldGroupStudents) {
    groupedStudents = _.map(groupedStudents, (options, label) => ({
      id: generateRandomId(),
      tag: label,
      label: t("common:all_students_with_label", {
        label,
      }),
      icon: (
        <Avatar
          size={"small"}
          shade={"light"}
          shape={"circle"}
          color={_.get(GENERIC_TAGS_INFO, `${label}.color`, "teal")}
          iconType={"group"}
        />
      ),
      options: _.map(options, option => {
        const {
          id,
          profileImage,
          isArchived = false,
          isArchivedV2 = false,
          tags = [],
        } = option || {};

        const studentName = getFormattedUserName({ user: option });

        const isStudentArchived = getUserArchivalStatus({
          isArchived,
          isArchivedV2,
        });

        return {
          ...option,
          title: studentName,
          titleRightComponent: (
            <div className={classes.tagsContainer}>
              {_.map(tags, item => {
                const { label } = item || {};
                return (
                  <Tag
                    key={label}
                    size={"xx-small"}
                    color={_.get(GENERIC_TAGS_INFO, `${label}.color`, "teal")}
                  >
                    {label}
                  </Tag>
                );
              })}
            </div>
          ),
          icon: (
            <ProfileCascade
              items={[{ id, value: studentName, imageUrl: profileImage }]}
              isArchived={isStudentArchived}
              showAvatar={true}
              avatarStyles={styles.avatarStyles}
            />
          ),
        };
      }),
    }));

    return {
      students: allStudents,
      groupedStudents: _.orderBy(groupedStudents, ["tag"], ["desc"]),
    };
  }

  return { students: allStudents };
};

const getSearchedStudents = ({ students, searchText }) => {
  return _.filter(students, student => {
    const fullName = getFormattedUserName({ user: student });

    const index = localSearch({ text: _.lowerCase(fullName), searchText });
    return index > -1 || _.isEmpty(searchText);
  });
};

export default compose(
  I18nHOC({ replace: "common" }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCourseUserMapQuery, {
    name: "getCourseUserMap",
    skip: ({ parentType }) => parentType === "Organization",
    options: ({
      parentId,
      portalType,
      courseUserMapFilters,
      userArchivedFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: parentId,
        portalType,
        filters: courseUserMapFilters,
        userArchivedFilters,
      },
    }),
    props({
      getCourseUserMap,
      ownProps: {
        isData,
        isLoading,
        t,
        parentId,
        courseUserMapFilters,
        curriculumProgramType,
        defaultStudentNameOrderPreference,
        userArchivedFilters,
      },
    }) {
      const courseData = getCourseUserMapFromCache({
        id: parentId,
        filters: courseUserMapFilters,
        userArchivedFilters,
      });

      return {
        getCourseUserMap,
        ...getStudents({
          t,
          data: _.get(courseData, "userMap.edges", []),
          curriculumProgramType,
          defaultStudentNameOrderPreference,
        }),
        isData: !_.isEmpty(courseData) && isData,
        isLoading:
          getCourseUserMap["networkStatus"] == 1 ||
          getCourseUserMap["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getAssignedStudentsQuery, {
    name: "getAssignedStudents",
    skip: ({ mode, assignmentId, isCurrentCourse, isDraft }) =>
      _.isEmpty(assignmentId) ||
      !mode ||
      mode === "create" ||
      !isCurrentCourse ||
      isDraft,
    options: ({ assignmentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: assignmentId,
      },
    }),
    props({
      getAssignedStudents,
      ownProps: { isData, isLoading, assignmentId, students: allStudents },
    }) {
      const students = getAssignedStudentsFromCache({
        id: assignmentId,
      });

      const assignedStudents = _.get(students, "assignedStudents", []);

      const assignmentStudents = _.map(
        assignedStudents,
        ({ student }) => student
      );
      const studentsUnion = _.unionBy(allStudents, assignmentStudents, "id");

      return {
        getAssignedStudents,
        students: studentsUnion,
        isData: !_.isEmpty(students) && isData,
        isLoading:
          getAssignedStudents["networkStatus"] == 1 ||
          getAssignedStudents["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(StudentSelection);
